// Shared helpers for the signup/login/forgot modal family.
// CustomExportLocalizations exposes resources/lang/<lang>/landing/signup.php
// as `landing:signup.*` to the JS layer.
const TR_PREFIX = 'landing:signup.';

// Mixin: every modal sub-component uses `t('key')` to look up translations
// under landing:signup.*. If Lang.js can't resolve the key it echoes the full
// `landing:signup.foo` path back — fall back to the bare key in that case so
// devs see something useful instead of the noisy placeholder.
export const i18nMixin = {
  methods: {
    t(key, params) {
      const raw = this.trans.get(TR_PREFIX + key, params);
      return raw === TR_PREFIX + key ? key : raw;
    },
  },
};

// /register, /login, /password.token are all CSRF-protected. We refresh the
// token right before each submit so the modal survives sessions that may
// have rotated the token (legacy admin flows, multi-tab usage, etc).
// Backend (CSRFController) returns `{ csrfToken: '...' }` at the top level —
// also update the <meta> tag so any non-Vue Blade form on the page can read
// it, matching what the legacy auth-cards do.
// AuthUserResource payload: account exists, verification mail was sent, inbox
// not confirmed yet. Checked before profile-step resume — getAuthStep() can still
// be 'birthday' when POST /register succeeded but PUT /register/update did not.
export function userAwaitingEmailVerification(user) {
  if (!user || !user.email) {
    return false;
  }
  if (user.email_verified_at || user.validated_at) {
    return false;
  }
  return !!user.is_verification_required;
}

export async function refreshCsrfToken() {
  try {
    const r = await axios.get(zRoute('ajax.csrf_token'));
    const t = r && r.data && r.data.csrfToken;
    if (!t) {
      return;
    }
    window.axios.defaults.headers.common['X-CSRF-TOKEN'] = t;
    const meta = document.querySelector('meta[name=csrf-token]');
    if (meta) {
      meta.setAttribute('content', t);
    }
  } catch (e) {
    /* non-fatal */
  }
}

export function oauthUrl(provider) {
  try {
    return zRoute('login.provider', provider);
  } catch (e) {
    return '/login/' + provider;
  }
}

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Password strength bars (viluu-signup / AccountStep) — purely a visual hint.
// The only validation requirement is the 8-char minimum (see isPasswordValid);
// uppercase/digit/length still nudge the strength meter higher.
export function passwordStrengthScore(password) {
  const p = password || '';
  if (!p) {
    return 0;
  }
  let s = 0;
  if (p.length >= 8) {
    s++;
  }
  if (/[A-Z]/.test(p)) {
    s++;
  }
  if (/[0-9]/.test(p)) {
    s++;
  }
  if (/[^A-Za-z0-9]/.test(p) || p.length >= 12) {
    s++;
  }
  return s;
}

export const PASSWORD_MIN_LENGTH = 8;

export function isPasswordValid(password) {
  return (password || '').length >= PASSWORD_MIN_LENGTH;
}

// Returns the user's age based on per-digit DOB inputs, or null if any digit
// is missing/invalid. Mirrors viluu-site/viluu-signup.js calcAge().
export function calcAge(day, month, year) {
  const d = parseInt(day, 10);
  const m = parseInt(month, 10);
  const y = parseInt(year, 10);
  if (!d || !m || !y) {
    return null;
  }
  if (y < 1900 || y > new Date().getFullYear()) {
    return null;
  }
  if (m < 1 || m > 12 || d < 1 || d > 31) {
    return null;
  }
  const today = new Date();
  let age = today.getFullYear() - y;
  const before =
    today.getMonth() + 1 < m || (today.getMonth() + 1 === m && today.getDate() < d);
  if (before) {
    age--;
  }
  return age;
}

// Step 1 DOB validation hint. `t` is the translator from i18nMixin; passing
// it in keeps this a pure function. Returns { text, ok, bad }.
export function getAgeHint({ day, month, year }, t) {
  const dStr = day || '';
  const mStr = month || '';
  const yStr = year || '';
  const d = parseInt(dStr, 10);
  const m = parseInt(mStr, 10);
  const y = parseInt(yStr, 10);
  const currentYear = new Date().getFullYear();

  if (!dStr && !mStr && !yStr) {
    return { text: t('age_hint'), ok: false, bad: false };
  }
  if (dStr && (d < 1 || d > 31)) {
    return { text: t('age_day_invalid'), ok: false, bad: true };
  }
  if (mStr && (m < 1 || m > 12)) {
    return { text: t('age_month_invalid'), ok: false, bad: true };
  }
  if (yStr.length === 4 && (y < 1900 || y > currentYear)) {
    return {
      text: t('age_year_range').replace(':year', currentYear),
      ok: false,
      bad: true,
    };
  }
  if (dStr && mStr && yStr.length === 4) {
    const daysInMonth = new Date(y, m, 0).getDate();
    if (d > daysInMonth) {
      return {
        text: t('age_days_in_month').replace(':month', m).replace(':days', daysInMonth),
        ok: false,
        bad: true,
      };
    }
  }
  if (!dStr || !mStr || yStr.length !== 4) {
    return { text: t('age_incomplete'), ok: false, bad: false };
  }

  const age = calcAge(day, month, year);
  if (age === null) {
    return { text: t('age_invalid'), ok: false, bad: true };
  }
  if (age < 18) {
    return { text: t('age_under18_with_age').replace(':age', age), ok: false, bad: true };
  }
  if (age > 100) {
    return { text: t('age_invalid'), ok: false, bad: true };
  }
  return { text: t('age_ok').replace(':age', age), ok: true, bad: false };
}
